class FreeShippingProgress extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseFloat(this.dataset.threshold || 0);
    this.oneTimeThreshold = parseFloat(this.dataset.oneTimeThreshold || 0);
    this.subscriptionThreshold = parseFloat(this.dataset.subscriptionThreshold || 0);
    this.previousProgress = null;
    this.isUpdating = false;
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    const messageEl = this.querySelector('[data-progress-message]');
    if (messageEl) {
      messageEl.classList.add('is-ready');
    }
    
    const progressBar = this.querySelector('[data-progress-fill]');
    if (progressBar) {
      const storedProgress = sessionStorage.getItem('freeShippingPreviousProgress');
      if (storedProgress !== null) {
        this.previousProgress = parseFloat(storedProgress);
        const currentProgress = progressBar.style.width ? parseFloat(progressBar.style.width) : null;
        if (currentProgress !== null && Math.abs(this.previousProgress - currentProgress) > 0.1) {
          progressBar.style.width = `${this.previousProgress}%`;
          progressBar.offsetHeight;
        }
      }
    }
    
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.updateProgress();
      });
    }
    
    this.updateProgress();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async updateProgress() {
    if (this.isUpdating) return Promise.resolve();
    
    this.isUpdating = true;
    const messageEl = this.querySelector('[data-progress-message]');
    const spinner = this.querySelector('.free-shipping-progress__spinner');
    
    if (spinner) {
      spinner.classList.remove('hidden');
    }

    try {
      const cartUrl = typeof routes !== 'undefined' ? routes.cart_url : '/cart';
      const response = await fetch(`${cartUrl}.js`);
      if (!response.ok) {
        this.isUpdating = false;
        if (spinner) spinner.classList.add('hidden');
        if (messageEl) messageEl.classList.add('is-ready');
        return Promise.resolve();
      }
      
      const cart = await response.json();
      const cartTotal = cart.total_price;
      const hasItems = cartTotal > 0;
      let progress = 0;

      // Check if cart has subscription items
      const hasSubscriptionItems = cart.items?.some(item => item.selling_plan_allocation != null) || false;
      
      // Use appropriate threshold based on cart contents
      const currentThreshold = hasSubscriptionItems ? this.subscriptionThreshold : this.oneTimeThreshold;
      
      // Update the threshold attribute if it changed
      if (this.threshold !== currentThreshold) {
        this.threshold = currentThreshold;
        this.dataset.threshold = currentThreshold.toString();
      }

      if (hasItems && this.threshold > 0) {
        progress = Math.min((cartTotal / this.threshold) * 100, 100);
        const remaining = Math.max(this.threshold - cartTotal, 0);
        const progressBar = this.querySelector('[data-progress-fill]');
        
        if (messageEl) {
          const amountEl = messageEl.querySelector('[data-amount]');
          
          if (remaining > 0) {
            const amount = this.formatMoney(remaining);
            if (amountEl) {
              amountEl.style.opacity = '0.5';
              requestAnimationFrame(() => {
                amountEl.textContent = amount;
                amountEl.style.opacity = '1';
              });
            } else {
              messageEl.innerHTML = `You're <span data-amount>${amount}</span> away from FREE shipping`;
            }
          } else {
            messageEl.textContent = "Congrats! You've got FREE shipping!";
          }
          messageEl.classList.add('is-ready');
        }
        
        if (progressBar) {
          const currentProgressFromStyle = progressBar.style.width ? parseFloat(progressBar.style.width) : null;
          const startProgress = this.previousProgress !== null ? this.previousProgress : currentProgressFromStyle;
          
          if (startProgress !== null && Math.abs(startProgress - progress) > 0.1) {
            setTimeout(() => {
              requestAnimationFrame(() => {
                progressBar.style.width = `${startProgress}%`;
                progressBar.offsetHeight;
                
                requestAnimationFrame(() => {
                  progressBar.style.width = `${progress}%`;
                });
              });
            }, 150);
          }
          
          this.previousProgress = progress;
          sessionStorage.setItem('freeShippingPreviousProgress', progress.toString());
        }
      }

      if (!hasItems) {
        this.style.display = 'none';
      } else {
        this.style.display = '';
      }
    } catch (error) {
      console.error('Error updating free shipping progress:', error);
      if (messageEl) {
        messageEl.classList.add('is-ready');
      }
    } finally {
      if (spinner) {
        spinner.classList.add('hidden');
      }
      this.isUpdating = false;
    }
    
    return Promise.resolve();
  }

  formatMoney(cents) {
    if (typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.money_format || '{{amount}}');
    }
    const currency = window.Shopify?.currency?.active || document.documentElement.getAttribute('data-currency') || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency,
    }).format(cents / 100);
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
